import { Div } from '@codesuma/baseline'
import { Slide, ISlide } from './slide'
import style from './index.module.css'

export interface ISliderFill {
    slide: ISlide
    index: number
}

// The strip has moved dx px from its resting place. ms is 0 while the finger
// is down and the animation's duration once the gesture lets go, so a listener
// can follow the same motion at its own speed.
export interface ISliderMove {
    dx: number
    ms: number
}

// An endless horizontal slider made of a recycling 3-slide window.
//
// The component knows nothing about months: it tracks a logical cursor
// (…, -1, 0, +1, …) and asks the parent to render content by emitting
// 'fill' with { slide, index }. The parent must call fill() once after
// subscribing to render the window — see fill() for the order it renders in.
//
// Direction mapping (direction() switches it, default 'rtl'): in RTL the
// NEXT index sits on the LEFT, so dragging the finger to the right (dx > 0)
// advances the cursor; LTR is the mirror image. With sign = +1 for RTL and
// -1 for LTR, the slide at physical position p holds logical index
// `cursor + sign * (1 - p)`.
//
// Events: 'fill' ({ slide, index }) — render this slide's content
//         'move' ({ dx, ms })      — the strip moved (see ISliderMove)
//         'change' (cursor)        — a swipe/next/prev committed
export const Slider = () => {
    const base = Div()
    base.addClass(style.base)

    const container = Div()
    container.addClass(style.container)
    base.append(container)

    const slides = [Slide(0), Slide(1), Slide(2)]
    container.append(...slides)

    let cursor = 0
    let busy = false
    let locked = false // see lock()
    let sign: 1 | -1 = 1 // +1 = rtl (next on the left), -1 = ltr

    // Gesture state
    let pointerId: number | null = null
    let ox = 0 // x origin (re-baselined when the horizontal lock engages)
    let oy = 0
    let dx = 0
    let startTime = 0
    let width = 0
    let lock: '' | 'h' | 'v' = ''

    base.on('pointerdown', (e: PointerEvent) => {
        if (busy || locked || pointerId !== null) return
        // A drag is about to bring a neighbour into view, so it has to exist.
        flush()
        pointerId = e.pointerId
        ox = e.clientX
        oy = e.clientY
        dx = 0
        lock = ''
        startTime = Date.now()
        width = base.el.getBoundingClientRect().width
    })

    base.on('pointermove', (e: PointerEvent) => {
        if (e.pointerId !== pointerId) return
        if (lock === 'v') return
        if (lock === '') {
            const mx = Math.abs(e.clientX - ox)
            const my = Math.abs(e.clientY - oy)
            if (my > 8 && my > mx) {
                lock = 'v' // vertical intent: leave the gesture to the browser
                return
            }
            if (mx > 8 && mx > my) {
                lock = 'h'
                ox = e.clientX // re-baseline so the slide starts moving from 0
                base.el.setPointerCapture(pointerId)
            } else {
                return
            }
        }
        dx = e.clientX - ox
        container.style({ transition: 'none', transform: `translateX(${dx}px)` })
        base.emit('move', { dx, ms: 0 })
    })

    base.on('pointerup', (e: PointerEvent) => {
        if (e.pointerId !== pointerId) return
        pointerId = null
        if (lock !== 'h') return
        const duration = Date.now() - startTime
        const commit = Math.abs(dx) > width / 4 || (duration < 300 && Math.abs(dx) > 30)
        settle(commit ? (dx > 0 ? 1 : -1) : 0)
    })

    // The browser takes over (e.g. vertical scroll via touch-action) — snap back.
    base.on('pointercancel', (e: PointerEvent) => {
        if (e.pointerId !== pointerId) return
        pointerId = null
        if (lock === 'h') settle(0)
    })

    // Animate the container to dir * width, then reset the transform and rotate
    // the slide positions in the same frame, and refill the slide that wrapped
    // around — all off-screen, so the window is always prev/current/next.
    function settle(dir: -1 | 0 | 1) {
        // next()/prev() reach here without a gesture, and they move a neighbour
        // into view too.
        flush()
        busy = true
        if (!width) width = base.el.getBoundingClientRect().width
        container.style({ transition: 'transform .25s ease', transform: `translateX(${dir * width}px)` })
        base.emit('move', { dx: dir * width, ms: 250 })
        setTimeout(() => { // fixed timer, not transitionend: a 0 → 0 settle never fires one
            container.style({ transition: 'none', transform: 'translateX(0)' })
            if (dir !== 0) {
                slides.forEach(s => s.recycle(dir))
                cursor += sign * dir
                const wrappedPos = dir > 0 ? 0 : 2 // the slide that jumped to the far side
                const wrapped = slides.find(s => s.getPos() === wrappedPos)!
                base.emit('fill', { slide: wrapped, index: cursor + sign * (1 - wrappedPos) })
                base.emit('change', cursor)
            }
            busy = false
        }, 260)
    }

    const fillOne = (slide: ISlide) => base.emit('fill', { slide, index: cursor + sign * (1 - slide.getPos()) })

    // Only the center slide is on screen; the other two are a swipe away at the
    // earliest. Rendering all three is most of what a launch spends before it
    // can show anything, so the neighbours wait for the main thread's first
    // quiet moment and the visible month goes up without them. Anything that
    // could bring a neighbour into view calls flush() first.
    let waiting: ISlide[] = []
    const flush = () => {
        const rest = waiting
        waiting = []
        rest.forEach(fillOne)
    }

    const fill = () => {
        const visible = slides.find(s => s.getPos() === 1)!
        waiting = slides.filter(s => s !== visible)
        fillOne(visible)
        // requestIdleCallback wherever there is one (every Android WebView, iOS
        // 15.4 and up), a plain timer where there is not. The timeout is the
        // floor: a thread that never falls idle must still end up with its
        // three months.
        if (window.requestIdleCallback) window.requestIdleCallback(flush, { timeout: 500 })
        else setTimeout(flush, 200)
    }

    const goto = (index: number) => {
        if (busy || cursor === index) return
        cursor = index
        fill()
        base.emit('change', cursor)
    }

    return Object.assign(base, {
        // Render the window (call once, after subscribing to 'fill'): the
        // visible slide now, its neighbours on the next quiet moment.
        fill,
        // Which side NEXT sits on; refill afterwards so the window matches
        direction(d: 'rtl' | 'ltr') {
            sign = d === 'ltr' ? -1 : 1
        },
        next() {
            if (!busy) settle(sign)
        },
        prev() {
            if (!busy) settle(sign === 1 ? -1 : 1)
        },
        // Jump straight to an index (no animation) and refill the window
        goto,
        // Jump straight back to where the slider started
        reset: () => goto(0),
        // While something else decides which index shows — the day's sheet
        // over the calendar — the slider stops following the finger. next(),
        // prev() and goto() still work.
        lock(on: boolean) {
            locked = on
        },
        getCursor() {
            return cursor
        },
    })
}

export type ISlider = ReturnType<typeof Slider>
